// MultiDoc design tokens.
//
// The single source of truth for colour, type, shape and elevation. The MUI
// theme (src/theme.ts) is built from these values, and the
// rules for using them live in DESIGN.md. Change a value here, never in a
// component.
//
// Idea: the calm, familiar structure of an office productivity suite (a
// brand-coloured header, a soft grey navigation pane, white cards with gentle
// layered shadows) with MultiDoc's own identity: MultiDoc blue, Noto Sans,
// softer corners. Neutral, status and shadow values follow the open Fluent 2
// web tokens; brand, type and shape are ours. Do not copy Microsoft trade dress
// (Segoe UI, the Microsoft 365 waffle, Microsoft's blue, their logos).

// ── Raw scales ───────────────────────────────────────────────────────────────

/** MultiDoc blue: a deep cobalt, our own (not Microsoft's azure). 80 is the default brand colour. */
export const brand = {
    10: '#050F26',
    20: '#081A3F',
    30: '#0B2459',
    40: '#0E2F73',
    50: '#10398C',
    60: '#1244A6',
    70: '#134CBA',
    80: '#1554CC',
    90: '#2F6BDD',
    100: '#4E83E6',
    110: '#6A97EB',
    120: '#86ABF0',
    130: '#A3C0F4',
    140: '#BED3F7',
    150: '#D8E4FA',
    160: '#EEF3FD',
} as const;

/** Fluent neutral greys, keyed by lightness (0 = black … 100 = white). */
export const grey = {
    4: '#0A0A0A',
    8: '#141414',
    12: '#1F1F1F',
    16: '#292929',
    20: '#333333',
    24: '#3D3D3D',
    26: '#424242',
    32: '#525252',
    38: '#616161',
    40: '#666666',
    44: '#707070',
    60: '#999999',
    68: '#ADADAD',
    74: '#BDBDBD',
    82: '#D1D1D1',
    84: '#D6D6D6',
    88: '#E0E0E0',
    90: '#E6E6E6',
    92: '#EBEBEB',
    94: '#F0F0F0',
    96: '#F5F5F5',
    98: '#FAFAFA',
    100: '#FFFFFF',
} as const;

/** Fluent shared status colours. */
export const green = { 10: '#F1FAF1', 20: '#9FD89F', 80: '#107C10', 90: '#0E700E', 100: '#54B054' } as const;
export const orange = { 10: '#FFF9F5', 20: '#FDCFB4', 80: '#BC4B09', 90: '#8A3707', 100: '#FAA06B' } as const;
export const red = { 10: '#FDF3F4', 20: '#EEACB2', 80: '#C50F1F', 90: '#B10E1C', 100: '#DC626D' } as const;

// ── Semantic colour roles ────────────────────────────────────────────────────

export interface ColorRoles {
    /** Page background behind cards. */
    bg: string;
    /** Cards, tables, dialogs, inputs. */
    surface: string;
    /** Table headers, wells, read-only fields. */
    sunken: string;
    /** Hover fill for rows, list items, subtle buttons. */
    hover: string;
    /** Divider between rows and inside cards. */
    rule: string;
    /** Input and outlined-button borders. */
    ruleStrong: string;
    /** Body text (Fluent foreground1). */
    ink: string;
    /** Secondary text: labels, metadata (foreground2/3). Passes WCAG AA. */
    inkSecondary: string;
    /** Placeholder / disabled. Decorative only. */
    inkMuted: string;
    /** Left navigation pane. Fixed; tenants cannot change it. */
    nav: string;
    navInk: string;
    navInkMuted: string;
    navRule: string;
    /** Selected item in the nav pane (a raised white tile). */
    navSelected: string;
    /** Suite header in dark mode (light mode uses the brand colour). */
    headerDark: string;
    /** Default brand colour when a tenant sets none. */
    accent: string;
    success: string;
    successBg: string;
    warning: string;
    warningBg: string;
    error: string;
    errorBg: string;
    info: string;
    infoBg: string;
}

export const light: ColorRoles = {
    bg: grey[98],
    surface: grey[100],
    sunken: grey[96],
    hover: grey[96],
    rule: grey[94],
    ruleStrong: grey[82],
    ink: '#242424',
    inkSecondary: grey[38],
    inkMuted: grey[74],
    nav: grey[94],
    navInk: '#242424',
    navInkMuted: grey[26],
    navRule: grey[88],
    navSelected: grey[100],
    headerDark: grey[4],
    accent: brand[80],
    success: green[80],
    successBg: green[10],
    warning: orange[80],
    warningBg: orange[10],
    error: red[80],
    errorBg: red[10],
    info: brand[70],
    infoBg: brand[160],
};

export const dark: ColorRoles = {
    bg: grey[12],
    surface: grey[16],
    sunken: grey[20],
    hover: grey[24],
    rule: grey[24],
    ruleStrong: grey[40],
    ink: grey[100],
    inkSecondary: grey[84],
    inkMuted: grey[44],
    nav: grey[8],
    navInk: grey[100],
    navInkMuted: grey[84],
    navRule: grey[20],
    navSelected: grey[20],
    headerDark: grey[4],
    accent: brand[100],
    success: green[100],
    successBg: 'rgba(84, 176, 84, 0.14)',
    warning: orange[100],
    warningBg: 'rgba(250, 160, 107, 0.14)',
    error: red[100],
    errorBg: 'rgba(220, 98, 109, 0.14)',
    info: brand[110],
    infoBg: 'rgba(98, 171, 245, 0.14)',
};

// ── Type ─────────────────────────────────────────────────────────────────────

export const font = {
    /** Noto Sans, self-hosted (src/design/fonts.css), so every user sees the same face. */
    sans: "'Noto Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
    /** Titles use the same family; kept separate so a display face can be swapped in. */
    display: "'Noto Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
    /** Code, hashes, long technical IDs. */
    mono: "'Cascadia Mono', Consolas, ui-monospace, SFMono-Regular, Menlo, monospace",
} as const;

// ── Shape & elevation ────────────────────────────────────────────────────────

/** Corner radii: a touch softer than Fluent's. Controls 6, cards/menus/dialogs 10. */
export const radius = { sm: 4, md: 6, lg: 8, xl: 10 } as const;

/**
 * Fluent layered shadows: an ambient 2px halo plus a key shadow. The number is
 * the blur of the key shadow — higher floats higher.
 */
export const elevation = {
    light: {
        s2: '0 0 2px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.14)',
        s4: '0 0 2px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.14)',
        s8: '0 0 2px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.14)',
        s16: '0 0 2px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.14)',
        s28: '0 0 8px rgba(0, 0, 0, 0.12), 0 14px 28px rgba(0, 0, 0, 0.14)',
        s64: '0 0 8px rgba(0, 0, 0, 0.12), 0 32px 64px rgba(0, 0, 0, 0.14)',
    },
    dark: {
        s2: '0 0 2px rgba(0, 0, 0, 0.24), 0 1px 2px rgba(0, 0, 0, 0.28)',
        s4: '0 0 2px rgba(0, 0, 0, 0.24), 0 2px 4px rgba(0, 0, 0, 0.28)',
        s8: '0 0 2px rgba(0, 0, 0, 0.24), 0 4px 8px rgba(0, 0, 0, 0.28)',
        s16: '0 0 2px rgba(0, 0, 0, 0.24), 0 8px 16px rgba(0, 0, 0, 0.28)',
        s28: '0 0 8px rgba(0, 0, 0, 0.24), 0 14px 28px rgba(0, 0, 0, 0.28)',
        s64: '0 0 8px rgba(0, 0, 0, 0.24), 0 32px 64px rgba(0, 0, 0, 0.28)',
    },
} as const;

/** Fluent motion: fast and decelerating. */
export const motion = {
    fast: '150ms cubic-bezier(0.33, 0, 0.1, 1)',
    base: '250ms cubic-bezier(0.33, 0, 0.1, 1)',
} as const;

/** Every token group in one object, for components that need a raw value
 *  (charts, canvas, SVG) where a theme palette path cannot reach. */
export const tokens = { brand, grey, green, orange, red, light, dark, font, radius, elevation, motion } as const;
